import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, CheckCircle, Phone, Mail, MapPin } from 'lucide-react';
export function ContactForm() {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    propertyType: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
    }, 1500);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormState({
      ...formState,
      [e.target.name]: e.target.value
    });
  };
  return <section id="contact" className="py-20 bg-teal-900 text-white relative overflow-hidden" data-id="element-9">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-teal-800/20 skew-x-12 transform translate-x-1/4" data-id="element-10" />

      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-11">
        <div className="flex flex-col lg:flex-row gap-16" data-id="element-12">
          {/* Contact Info */}
          <div className="lg:w-5/12" data-id="element-13">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-14">
              Get Your Free Quote Today
            </h2>
            <p className="text-teal-100 text-lg mb-10 leading-relaxed" data-id="element-15">
              Fill out the form and we'll get back to you within 24 hours with a
              tailored price for your cleaning needs.
            </p>

            <div className="space-y-8" data-id="element-16">
              <div className="flex items-start gap-4" data-id="element-17">
                <div className="w-12 h-12 rounded-full bg-teal-800 flex items-center justify-center flex-shrink-0" data-id="element-18">
                  <Phone className="w-6 h-6 text-teal-300" data-id="element-19" />
                </div>
                <div data-id="element-20">
                  <h3 className="text-xl font-bold mb-1" data-id="element-21">Call Us Directly</h3>
                  <p className="text-teal-200 mb-2" data-id="element-22">
                    Immediate assistance for urgent jobs
                  </p>
                  <a href="tel:07355854313" className="text-2xl font-bold hover:text-teal-300 transition-colors" data-id="element-23">
                    07355 854313
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-24">
                <div className="w-12 h-12 rounded-full bg-teal-800 flex items-center justify-center flex-shrink-0" data-id="element-25">
                  <Mail className="w-6 h-6 text-teal-300" data-id="element-26" />
                </div>
                <div data-id="element-27">
                  <h3 className="text-xl font-bold mb-1" data-id="element-28">Email Us</h3>
                  <p className="text-teal-200 mb-2" data-id="element-29">
                    Send us your requirements
                  </p>
                  <a href="mailto:Cleencoglasgow@gmail.com" className="text-lg hover:text-teal-300 transition-colors" data-id="element-30">
                    Cleencoglasgow@gmail.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-31">
                <div className="w-12 h-12 rounded-full bg-teal-800 flex items-center justify-center flex-shrink-0" data-id="element-32">
                  <MapPin className="w-6 h-6 text-teal-300" data-id="element-33" />
                </div>
                <div data-id="element-34">
                  <h3 className="text-xl font-bold mb-1" data-id="element-35">Service Area</h3>
                  <p className="text-teal-200" data-id="element-36">
                    Proudly serving Glasgow City Centre, West End, Southside,
                    and surrounding areas.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:w-7/12" data-id="element-37">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-white rounded-2xl p-8 md:p-10 shadow-2xl" data-id="element-38">
              {isSuccess ? <div className="text-center py-12" data-id="element-39">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-40">
                    <CheckCircle className="w-10 h-10 text-green-600" data-id="element-41" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-42">
                    Quote Request Received!
                  </h3>
                  <p className="text-gray-600 mb-8" data-id="element-43">
                    Thank you for contacting Cleen Co. We'll be in touch shortly
                    to discuss your requirements.
                  </p>
                  <button onClick={() => setIsSuccess(false)} className="text-teal-600 font-bold hover:underline" data-id="element-44">
                    Send another request
                  </button>
                </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-45">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-46">
                    <div data-id="element-47">
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-48">
                        Full Name
                      </label>
                      <input type="text" id="name" name="name" required className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-gray-900" placeholder="John Doe" value={formState.name} onChange={handleChange} data-id="element-49" />
                    </div>
                    <div data-id="element-50">
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-51">
                        Phone Number
                      </label>
                      <input type="tel" id="phone" name="phone" required className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-gray-900" placeholder="07700 900000" value={formState.phone} onChange={handleChange} data-id="element-52" />
                    </div>
                  </div>

                  <div data-id="element-53">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-54">
                      Email Address
                    </label>
                    <input type="email" id="email" name="email" required className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-gray-900" placeholder="john@example.com" value={formState.email} onChange={handleChange} data-id="element-55" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-56">
                    <div data-id="element-57">
                      <label htmlFor="propertyType" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-58">
                        Property Type
                      </label>
                      <select id="propertyType" name="propertyType" className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-gray-900 bg-white" value={formState.propertyType} onChange={handleChange} data-id="element-59">
                        <option value="" data-id="element-60">Select Type</option>
                        <option value="Residential" data-id="element-61">Residential Home</option>
                        <option value="Commercial" data-id="element-62">Commercial / Office</option>
                        <option value="Landlord" data-id="element-63">Landlord / Rental</option>
                        <option value="Construction" data-id="element-64">Construction Site</option>
                      </select>
                    </div>
                    <div data-id="element-65">
                      <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-66">
                        Service Needed
                      </label>
                      <select id="service" name="service" className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-gray-900 bg-white" value={formState.service} onChange={handleChange} data-id="element-67">
                        <option value="" data-id="element-68">Select Service</option>
                        <option value="End of Tenancy" data-id="element-69">
                          End of Tenancy Cleaning
                        </option>
                        <option value="Office Cleaning" data-id="element-70">Office Cleaning</option>
                        <option value="Deep Cleaning" data-id="element-71">Deep Cleaning</option>
                        <option value="Move In/Out" data-id="element-72">
                          Move In/Out Cleaning
                        </option>
                        <option value="Construction" data-id="element-73">
                          Construction Cleaning
                        </option>
                        <option value="Other" data-id="element-74">Other</option>
                      </select>
                    </div>
                  </div>

                  <div data-id="element-75">
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-76">
                      Message (Optional)
                    </label>
                    <textarea id="message" name="message" rows={4} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-gray-900 resize-none" placeholder="Tell us about your cleaning needs..." value={formState.message} onChange={handleChange} data-id="element-77" />
                  </div>

                  <button type="submit" disabled={isSubmitting} className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold py-4 rounded-lg transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-78">
                    {isSubmitting ? 'Processing...' : <>
                        Request Free Quote <Send className="w-5 h-5" data-id="element-79" />
                      </>}
                  </button>
                </form>}
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}